import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
  type MutationFunction,
  type QueryKey,
} from "@tanstack/react-query";
import { message, type FormInstance } from "antd";
interface ApiValidationErrorResponse {
  type?: string;
  title?: string;
  status?: number;
  errors?: Record<string, string[]>;
}
interface AppMutationConfig<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown
> {
  successMessage?: string;
  errorMessagePrefix?: string;
  invalidateQueryKey?: QueryKey;
  SecondQueryKey?: QueryKey | string | unknown[];
  // Allow passing through all other useMutation options
  options?: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    "mutationFn" | "onSuccess" | "onError"
  >;
  onSuccess?: (data: TData, variables: TVariables, context: TContext | undefined) => void;
  onError?: (error: TError, variables: TVariables, context: TContext | undefined) => void;
  form?: FormInstance;
}

export function useAppMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown
>(
  mutationFn: MutationFunction<TData, TVariables>,
  config?: AppMutationConfig<TData, TError, TVariables, TContext>
) {
  const queryClient = useQueryClient();

  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn,
    onSuccess: (data, variables, context) => {
      if (config?.successMessage) {
        message.success(config.successMessage);
      }
      if (config?.invalidateQueryKey) {
        queryClient.invalidateQueries({ queryKey: config.invalidateQueryKey });
      }
      if (config?.SecondQueryKey) {
            queryClient.refetchQueries({
                queryKey: Array.isArray(config.SecondQueryKey) ? config.SecondQueryKey : [config.SecondQueryKey],
                exact: false,
            });
        }
      config?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
     const axiosError = error as { response?: { data?: ApiValidationErrorResponse } };
  const data = axiosError.response?.data;

  if (data?.errors) {
    // If caller provided a form instance, set field-level errors
    if (config?.form) {
      const form = config.form;
      const allFormFields = Object.keys(form.getFieldsValue(true));
      const fieldErrors = Object.entries(data.errors).map(([field, messages]) => {
        const formFieldName = allFormFields.find(
          (name) => name.toLowerCase() === field.toLowerCase()
        );
        return {
          name: formFieldName || field,
          errors: messages,
        };
      });
      form.setFields(fieldErrors);
      if (fieldErrors.length > 0) {
        setTimeout(() => {
          const firstErrorField = fieldErrors[0].name;
          form.scrollToField(firstErrorField, {
            behavior: 'smooth',
            block: 'center',
          });
        }, 100);
      }
    } else {
      // Otherwise show as global error
      const messages = Object.entries(data.errors)
        .map(([field, errs]) => `${field}: ${errs.join(", ")}`)
        .join("\n");
      message.error({
        content: messages,
        duration: 10,
        style: { whiteSpace: "pre-line" }, // line breaks
      });
    }
  } else if (data?.title) {
    message.error({
      content: data.title,
      duration: 10,
    });
  } else {
    message.error(
      `${config?.errorMessagePrefix || "Operation failed"}: ${(error as Error).message}`
    );
  }
      config?.onError?.(error, variables, context);
    },
    ...(config?.options || {}),
  });
}